import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiErrorMessage, authApi, coursesApi, type SignupDetails } from '../../api/client'
import toast from 'react-hot-toast'

export default function RegisterPage() {
  const [params] = useSearchParams()
  const [method, setMethod] = useState<'google' | 'email'>('google')
  const [form, setForm] = useState<SignupDetails & { email: string; password: string }>({
    username: '', email: '', password: '', full_name: '', company: '', city: '',
    country: '', address: '', course_slug: params.get('course') || '',
  })
  const navigate = useNavigate()
  const setup = useQuery({ queryKey: ['setup-status'], queryFn: () => authApi.setupStatus().then(res => res.data) })
  const courses = useQuery({ queryKey: ['signup-courses'], queryFn: coursesApi.signupOptions })
  const selected = courses.data?.find(course => course.slug === form.course_slug)
  const locked = !setup.data || setup.data.setup_required

  const registerMut = useMutation({
    mutationFn: async () => {
      if (method === 'email') {
        await authApi.register(form)
        return null
      }
      const { email: _email, password: _password, ...details } = form
      return (await authApi.googleSignup(details)).data
    },
    onSuccess: (flow) => {
      if (flow) {
        window.location.assign(flow.redirect_url)
      } else {
        toast.success('Check your email for the verification code!')
        navigate(`/verify-email?email=${encodeURIComponent(form.email)}`)
      }
    },
    onError: (error: unknown) => toast.error(apiErrorMessage(error, 'Registration failed')),
  })

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(previous => ({ ...previous, [key]: event.target.value }))

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <Link to="/" className="font-extrabold text-2xl no-underline">PacketFive Academy</Link>
          <h1 className="text-2xl font-extrabold mt-4">Sign up for a course</h1>
          <p className="text-brand-muted text-sm mt-1">Choose a course and provide your details before creating an account.</p>
        </div>

        {setup.error && <p className="card mb-4" role="alert">{apiErrorMessage(setup.error, 'Could not check registration availability. Please reload.')}</p>}
        {setup.data?.setup_required && <div className="card mb-4" role="alert">Platform setup is in progress. Registration is disabled. <Link to="/login" className="text-brand-blue">Admin login</Link></div>}
        {courses.error && <p className="card mb-4" role="alert">{apiErrorMessage(courses.error, 'Could not load courses. Please reload.')}</p>}
        {courses.data?.length === 0 && <p className="card mb-4">No courses are currently open for enrolment. Signup opens when a course opens for enrolment.</p>}

        <form className="card space-y-4" onSubmit={event => { event.preventDefault(); registerMut.mutate() }}>
          <div>
            <label htmlFor="course" className="label">Selected course</label>
            <select id="course" name="course_slug" className="input" value={form.course_slug} onChange={set('course_slug')} required disabled={courses.isLoading}>
              <option value="">Select a course</option>
              {courses.data?.map(course => <option key={course.id} value={course.slug}>{course.title} ({course.is_free ? 'Free' : 'Paid'})</option>)}
            </select>
          </div>
          {selected && <p className="text-sm text-brand-muted">{selected.is_free
            ? 'Your account will be enrolled in this free course. Email accounts must verify their email before accessing lessons.'
            : 'Selecting this paid course does not charge you or unlock content. Payment and administrator approval are required; online checkout is not currently available.'}</p>}

          {([
            ['full_name', 'Full name', 255],
            ['username', 'Username', 80],
            ['company', 'Company or organisation (use Independent if none)', 255],
            ['city', 'City', 100],
            ['country', 'Country', 100],
          ] as const).map(([key, label, maxLength]) => (
            <div key={key}>
              <label htmlFor={key} className="label">{label}</label>
              <input id={key} name={key} className="input" value={form[key]} onChange={set(key)} required maxLength={maxLength}
                minLength={key === 'username' ? 3 : key === 'company' ? 1 : 2} />
            </div>
          ))}
          <div>
            <label htmlFor="address" className="label">Address</label>
            <textarea id="address" name="address" className="input" value={form.address} onChange={set('address')} required minLength={5} maxLength={500} rows={3} />
          </div>
          <div>
            <label htmlFor="method" className="label">Signup method</label>
            <select id="method" className="input" value={method} onChange={event => setMethod(event.target.value === 'google' ? 'google' : 'email')}>
              <option value="google">Google</option>
              <option value="email">Email and password</option>
            </select>
          </div>
          {method === 'email' ? <>
            <div>
              <label htmlFor="email" className="label">Email</label>
              <input id="email" name="email" className="input" type="email" value={form.email} onChange={set('email')} required />
            </div>
            <div>
              <label htmlFor="password" className="label">Password</label>
              <input id="password" name="password" className="input" type="password" value={form.password} onChange={set('password')} required minLength={8} maxLength={128} />
            </div>
          </> : <p className="text-xs text-brand-muted">Google will verify your email after you submit this signup form.</p>}
          <p className="text-xs text-brand-muted">Your contact and organisation details are kept in your account and are not shown in the public course catalogue.</p>
          <button type="submit" className="btn-primary w-full justify-center" disabled={registerMut.isPending || locked || !selected || !!courses.error}>
            {registerMut.isPending ? 'Submitting...' : method === 'google' ? 'Sign up with Google' : 'Create account and select course'}
          </button>
          <p className="text-center text-sm text-brand-muted">Already registered? <Link to="/login" className="text-brand-blue">Sign in</Link></p>
        </form>
      </div>
    </div>
  )
}
